import { memo, useId } from 'react';
import { alpha, Box, useTheme } from '@mui/material';

type MfaShieldHeroProps = {
  tone?: 'primary' | 'success';
  size?: number;
  /** `hero` paints the gradient band used on full cards; `inline` is the bare badge for tight layouts. */
  variant?: 'hero' | 'inline';
};

/**
 * Hero illustration for the MFA screens. Drawn inline instead of shipping an asset so it
 * follows the white-label palette (`primary.main` changes per tenant) and both theme modes.
 */
const MfaShieldHero = ({ tone = 'primary', size = 104, variant = 'hero' }: MfaShieldHeroProps) => {
  const { palette } = useTheme();
  const gradientId = useId();
  const glossId = useId();
  const color = tone === 'success' ? palette.success.main : palette.primary.main;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: variant === 'hero' ? 4 : 0,
        background:
          variant === 'hero'
            ? `linear-gradient(180deg, ${alpha(color, 0.16)} 0%, ${alpha(color, 0)} 100%)`
            : 'none',
      }}
    >
      <Box
        component='svg'
        viewBox='0 0 64 72'
        role='presentation'
        sx={{
          width: size,
          height: 'auto',
          filter: `drop-shadow(0 8px 16px ${alpha(color, 0.35)})`,
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={alpha(color, 0.95)} />
            <stop offset='100%' stopColor={color} />
          </linearGradient>
          <linearGradient id={glossId} x1='0' y1='0' x2='1' y2='1'>
            <stop offset='0%' stopColor='#fff' stopOpacity='0.35' />
            <stop offset='60%' stopColor='#fff' stopOpacity='0' />
          </linearGradient>
        </defs>
        <path
          d='M32 2 L60 12 V34 C60 51 48 63 32 70 C16 63 4 51 4 34 V12 Z'
          fill={`url(#${gradientId})`}
        />
        <path d='M32 2 L60 12 V34 C60 51 48 63 32 70 Z' fill={`url(#${glossId})`} />
        <path
          d='M32 8 L54 16 V34 C54 47.5 44.5 57.5 32 63.5 C19.5 57.5 10 47.5 10 34 V16 Z'
          fill='none'
          stroke='#fff'
          strokeOpacity='0.35'
          strokeWidth='1.5'
        />
        <path
          d='M25 31 V25 A7 7 0 0 1 39 25 V31'
          fill='none'
          stroke='#fff'
          strokeWidth='3.5'
          strokeLinecap='round'
        />
        <rect x='21' y='31' width='22' height='17' rx='3.5' fill='#fff' />
        <circle cx='32' cy='38' r='2.6' fill={color} />
        <rect x='30.7' y='39' width='2.6' height='5.5' rx='1.3' fill={color} />
      </Box>
    </Box>
  );
};

export default memo(MfaShieldHero);
